// Add js-focus-visible class to outer HTML (https://github.com/WICG/focus-visible)
import 'focus-visible';
import './index.module.scss';
import {
    collectEvent,
    UserAction,
    userActionTypeEnum,
} from '@thoughtspot/metrics';
import * as systemConfigService from '@thoughtspot/system-config-service';
import * as metricsService from '/@services/metrics-service/metrics-service';
import * as customStylingService from '/@services/system/config-service/custom-styling-service';
import * as sessionService from '/@services/system/config-service/session-service';
import * as webStorageService from '/@services/system/config-service/web-storage-service';
import {
    onEmbedInit,
    postInitialAppRender,
    preAppRenderAsync,
    preAppRenderSync,
} from '/@services/system/lifecycle-hooks-service/lifecycle-hooks-service';
import { getNavService } from '/@services/system/nav-service/nav-service';
import * as observabilityService from '/@services/system/observability-service/observability-service';
import { overrideBrowserRequest } from '/@utils/http-request-polyfill';
import { PERF_EVENT,PERF_KEYS, setPerfLog } from '/@utils/perf-log';
import { getRenderRoot } from '/@utils/react-base.util';
import { App, getMainRouteObject } from './app';
import { renderApp } from './blink-v2.util';
import {
    forceReloadWhenOAuthTriggered,
    stringOverrideLoader,
} from './services/system/lifecycle-hooks-service/lifecycle-hooks-utils';
import {
    EventType as EmbedEventType,
    isEmbeddedApp,
    parentOrigin,
    parentProtocol,
    sdkVersion,
    versionToBeSkipped,
} from './utils/embed-base.util';
import { initializePerfLog } from './utils/perf-log/perf-log';
import { preloadComponents } from './utils/preload-routes';

if (process.env.NODE_ENV === 'development') {
    await import('./wdyr');
}

const captureBlinkCdnMetrics = () => {
    // eslint-disable-next-line no-underscore-dangle
    if ((window as any).__cdnHealthCheckStatus === 'failed') {
        collectEvent(
            new UserAction(userActionTypeEnum.CDN_HEALTH_CHECK_STATUS),
            {
                status: 'Failed',
                buildTag: sessionService.getReleaseVersion(),
                clusterId: sessionService.getClusterId(),
                clusterName: sessionService.getClusterName(),
            },
        );
    } else {
        collectEvent(
            new UserAction(userActionTypeEnum.CDN_HEALTH_CHECK_STATUS),
            {
                status: 'Healthy',
                buildTag: sessionService.getReleaseVersion(),
                clusterId: sessionService.getClusterId(),
                clusterName: sessionService.getClusterName(),
            },
        );
    }
};

async function startPerforormanceMetric() {
    const webVitals = await import('web-vitals');

    function sendToAnalytics(metric: any) {
        const { name, value } = metric;
        collectEvent(
            new UserAction(userActionTypeEnum.INITIAL_PAGE_LOAD_METRICS),
            {
                metricType: name,
                valueInMS: value,
            },
        );
        if (typeof performance?.mark === 'function')
            performance.mark(`mixpanel-${name}`, {
                detail: {
                    value,
                },
            });
    }

    webVitals.getCLS(sendToAnalytics);
    webVitals.getFID(sendToAnalytics);
    webVitals.getFCP(sendToAnalytics);
    webVitals.getTTFB(sendToAnalytics);
    webVitals.getLCP(metric => {
        sendToAnalytics(metric);
    });
}

const emitEmbedAppInit = async () => {
    if (isEmbeddedApp()) {
        const EventBridge = await import('./utils/event-bridge/event-bridge');
        if (sdkVersion() && versionToBeSkipped()) {
            EventBridge.setParentOrigin(parentOrigin(), parentProtocol());
            const response = await EventBridge.dispatchNonMemoEvent(
                EmbedEventType.AppInit,
                {},
            );
            // eslint-disable-next-line promise/always-return
            await onEmbedInit((response as any)?.data);

            return (response as any)?.data;
        }
    }
    return null;
};

/* Do not update this function. Add your function call
 * to corresponding lifecycle hook in lifecycle-hooks-service.ts
 */
async function appInit() {
    setPerfLog(PERF_KEYS.APP_INIT_START, PERF_EVENT.START);
    if (typeof performance?.mark === 'function')
        performance.mark(
            new UserAction(userActionTypeEnum.APP_INITIALIZED).name,
        );
    // Preload the component for the current route
    setPerfLog(PERF_KEYS.APP_PRELOAD_INITIATED, PERF_EVENT.START);
    preloadComponents(getMainRouteObject());
    setPerfLog(PERF_KEYS.APP_PRELOAD_INITIATED, PERF_EVENT.COMPLETE);
    // initialize string overrides from url params
    stringOverrideLoader();

    if (forceReloadWhenOAuthTriggered()) {
        return;
    }

    // Before app renders
    const embedData = await emitEmbedAppInit();
    // Initialize the Custom fetch and XHR
    overrideBrowserRequest({
        isEmbedApp: isEmbeddedApp(),
        hasAuthToken: !!embedData?.authToken,
        embedExpiryInAuthToken: embedData?.embedExpiryInAuthToken,
    });

    setPerfLog(PERF_KEYS.PRE_APP_RENDER_ASYNC, PERF_EVENT.START);
    await preAppRenderAsync();
    setPerfLog(PERF_KEYS.PRE_APP_RENDER_ASYNC, PERF_EVENT.COMPLETE);
    preAppRenderSync();

    // App render
    await renderApp(getRenderRoot(), App, {
        metricsService,
        sessionService,
        systemConfigService,
        customStylingService,
        webStorageService,
        observabilityService,
        navService: getNavService(),
    });

    // After App renders
    postInitialAppRender();
    startPerforormanceMetric();
    captureBlinkCdnMetrics();
}

initializePerfLog();
appInit();
